import React, { useState, useEffect } from 'react';
import batchService from '../api/batchService';
import programService from '../api/programService'; // To populate dropdown
import centerService from '../api/centerService';   // To populate dropdown
import holidayService from '../api/holidayService'; // For holiday validation
import trainerService from '../api/trainerService'; // For coordinator dropdown
import authService from '../api/authService';
import { parseLocalDate } from '../utils/dateUtils';
import './ManagementPages.css';
import './Modal.css';

const BatchManagement = () => {
  const [batches, setBatches] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [centers, setCenters] = useState([]);
  const [trainers, setTrainers] = useState([]); // For coordinator dropdown
  const [holidays, setHolidays] = useState([]); // For holiday validation
  const [holidayWarning, setHolidayWarning] = useState(null); // Warning message for holiday dates
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Batch timing presets
  const BATCH_TIMINGS = {
    morning: { start_time: '10:00 AM', end_time: '01:00 PM' },
    afternoon: { start_time: '02:00 PM', end_time: '05:00 PM' }
  };

  const initialFormState = {
    id: null,
    batch_name: '',
    start_date: '',
    programId: '',
    centerId: '',
    batch_type: 'morning',
    start_time: '10:00 AM',
    end_time: '01:00 PM',
    coordinatorId: ''
  };
  const [formData, setFormData] = useState(initialFormState);

  const isScheduler = authService.getCurrentUser()?.role === 'scheduler'

  useEffect(() => {
    // Fetch all data needed for the page
    setLoading(true);
    const currentYear = new Date().getFullYear();
    Promise.allSettled([
      batchService.getAllBatches(),
      programService.getAllPrograms(),
      centerService.getAllCenters(),
      trainerService.getAllTrainers(),
      holidayService.getAllHolidays(currentYear),
      holidayService.getAllHolidays(currentYear + 1) // Also fetch next year's holidays
    ]).then(([batchesRes, programsRes, centersRes, trainersRes, holidaysRes, nextYearHolidaysRes]) => {
      if (batchesRes.status === 'fulfilled') {
        setBatches(batchesRes.value.data);
      }
      if (programsRes.status === 'fulfilled') {
        setPrograms(programsRes.value.data);
      }
      if (centersRes.status === 'fulfilled') {
        setCenters(centersRes.value.data);
      }
      if (trainersRes.status === 'fulfilled') {
        setTrainers(trainersRes.value.data);
      }
      // Combine holidays from both years
      const allHolidays = [];
      if (holidaysRes.status === 'fulfilled' && holidaysRes.value.data.success) {
        allHolidays.push(...holidaysRes.value.data.data);
      }
      if (nextYearHolidaysRes.status === 'fulfilled' && nextYearHolidaysRes.value.data.success) {
        allHolidays.push(...nextYearHolidaysRes.value.data.data);
      }
      setHolidays(allHolidays);
    }).finally(() => setLoading(false));
  }, []);

  // Helper to convert 24h time (HH:MM) to 12h format (HH:MM AM/PM)
  const convertTo12Hour = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const h = parseInt(hours, 10);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12.toString().padStart(2, '0')}:${minutes} ${suffix}`;
  };

  // Helper to convert 12h time (HH:MM AM/PM) to 24h format (HH:MM)
  const convertTo24Hour = (time12) => {
    if (!time12) return '';
    const match = time12.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!match) return time12;
    let [, hours, minutes, period] = match;
    hours = parseInt(hours, 10);
    if (period.toUpperCase() === 'PM' && hours !== 12) hours += 12;
    if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;
    return `${hours.toString().padStart(2, '0')}:${minutes}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let updates = { [name]: value };



    // Auto-fill times when batch_type changes (for morning/afternoon presets)
    if (name === 'batch_type') {
      if (BATCH_TIMINGS[value]) {
        updates.start_time = BATCH_TIMINGS[value].start_time;
        updates.end_time = BATCH_TIMINGS[value].end_time;
      }
      // For custom, keep existing times or set defaults
      if (value === 'custom' && !formData.start_time) {
        updates.start_time = '09:00 AM';
        updates.end_time = '12:00 PM';
      }
    }

    // Handle custom time inputs (convert from 24h input to 12h storage)
    if (name === 'start_time_input') {
      updates = { start_time: convertTo12Hour(value) };
    }
    if (name === 'end_time_input') {
      updates = { end_time: convertTo12Hour(value) };
    }

    // Check for holiday when start_date changes
    if (name === 'start_date' && value) {
      const holiday = holidays.find(h => h.date === value);
      if (holiday) {
        setHolidayWarning(`Warning: ${value} is a holiday (${holiday.name}). You can still select this date if needed.`);
      } else {
        setHolidayWarning(null);
      }
    }

    setFormData({ ...formData, ...updates });
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: '' });
    }
  };

  // Modal handlers
  const handleOpenAddModal = () => {
    setIsEditing(false);
    setFormData(initialFormState);
    setFieldErrors({});
    setHolidayWarning(null);
    setShowModal(true);
  };

  const handleOpenEditModal = (batch) => {
    setFormData({
      id: batch.id,
      batch_name: batch.batch_name,
      batch_code: batch.batch_code || '',
      start_date: batch.start_date ? batch.start_date.split('T')[0] : '',
      programId: batch.programId || '',
      centerId: batch.centerId || '',
      batch_type: batch.batch_type || 'morning',
      start_time: batch.start_time || '10:00 AM',
      end_time: batch.end_time || '01:00 PM',
      coordinatorId: batch.coordinatorId || ''
    });
    setIsEditing(true);
    setFieldErrors({});
    setHolidayWarning(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsEditing(false);
    setFormData(initialFormState);
    setFieldErrors({});
    setHolidayWarning(null);
  };

  // Parse error response and extract field-level errors
  const parseErrors = (error) => {
    const errors = {};
    const response = error.response?.data;

    if (response?.errors && Array.isArray(response.errors)) {
      response.errors.forEach(err => {
        const field = err.path || err.param || err.field;
        if (field) {
          errors[field] = err.msg || err.message;
        }
      });
    } else if (response?.message) {
      const msg = response.message.toLowerCase();
      if (msg.includes('batch_code') || msg.includes('batch code')) {
        errors.batch_code = response.message;
      } else if (msg.includes('batch_name') || msg.includes('name')) {
        errors.batch_name = response.message;
      } else if (msg.includes('start_date') || msg.includes('date')) {
        errors.start_date = response.message;
      } else if (msg.includes('program')) {
        errors.programId = response.message;
      } else if (msg.includes('center')) {
        errors.centerId = response.message;
      } else {
        errors._general = response.message;
      }
    } else {
      errors._general = 'Could not save batch. Please check your input.';
    }
    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFieldErrors({});

    const action = isEditing
      ? batchService.updateBatch(formData.id, formData)
      : batchService.createBatch(formData);

    action.then(() => {
      batchService.getAllBatches().then(res => setBatches(res.data));
      handleCloseModal();
    }).catch(err => {
      const errors = parseErrors(err);
      setFieldErrors(errors);
    });
  };

  const handleDelete = (id) => {
    if (window.confirm('🗑️ Are you sure you want to delete this batch? This action cannot be undone.')) {
      batchService.deleteBatch(id).then(() => {
        batchService.getAllBatches().then(res => setBatches(res.data));
        alert('✅ Batch deleted successfully!');
      }).catch(err => {
        alert(`❌ Error deleting batch: ${err.response?.data?.message || err.message}`);
      });
    }
  };
  
  // Calculate stats for compact header
  const totalBatches = batches.length;
  const activeBatches = batches.filter(b => new Date(b.start_date) <= new Date()).length;

  return (
    <div className="management-page">
      {/* Compact Header with inline stats and Add button */}
      <div className="page-header-compact">
        <div className="header-left">
          <h1 className="page-title">
            <span className="page-title-icon">📚</span> Batch Management
          </h1>
          <div className="inline-stats">
            <span className="stat-item">📊 {totalBatches} Batches</span>
            <span className="stat-item">🟢 {activeBatches} Active</span>
          </div>
        </div>
        {isScheduler && (
          <button className="add-btn-primary" onClick={handleOpenAddModal}>
            + Add Batch
          </button>
        )}
      </div>
      
      {loading ? (
        <div className="loading-spinner fade-in">
          <div className="spinner"></div>
          <span>Loading batch data...</span>
        </div>
      ) : (
        <table className="management-table fade-in">
          <thead>
            <tr>
              <th>Batch Name</th>
              <th>Code</th>
              <th>Program</th>
              <th>Center</th>
              <th>Start Date</th>
              <th>Timing</th>
              <th>Coordinator</th>
              {isScheduler && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {batches.map(batch => (
              <tr key={batch.id}>
                <td>
                  <strong>{batch.batch_name}</strong>
                </td>
                <td><code>{batch.batch_code || '-'}</code></td>
                <td>{batch.program?.program_name || 'N/A'}</td>
                <td>{batch.center?.name || 'N/A'}</td>
                <td>{parseLocalDate(batch.start_date)?.toLocaleDateString() || '-'}</td>
                <td>
                  <span className={`timing-badge ${batch.batch_type || 'morning'}`}>
                    {batch.batch_type === 'custom'
                      ? '⏰ Custom'
                      : batch.batch_type === 'afternoon'
                        ? '🌤️ Afternoon'
                        : '🌅 Morning'}
                  </span>
                  <br />
                  <small>{batch.start_time || '10:00 AM'} - {batch.end_time || '01:00 PM'}</small>
                </td>
                <td>{batch.coordinator?.name || '-'}</td>
                {isScheduler && (
                  <td className="actions-cell">
                    <button
                      onClick={() => handleOpenEditModal(batch)}
                      className="action-button edit"
                      title="Edit batch"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDelete(batch.id)}
                      className="action-button delete"
                      title="Delete batch"
                    >
                      🗑️ Delete
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal Dialog */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{isEditing ? '✏️ Edit Batch' : '➕ Add New Batch'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>✕</button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              {fieldErrors._general && (
                <div className="form-error-banner">
                  ⚠️ {fieldErrors._general}
                </div>
              )}
              <div className="form-grid">
                <div className={`form-field ${fieldErrors.batch_name ? 'has-error' : ''}`}>
                  <label>📖 Batch Name</label>
                  <input
                    name="batch_name"
                    value={formData.batch_name}
                    onChange={handleInputChange}
                    placeholder="e.g., Center City, Aug 2025"
                    required
                    className={fieldErrors.batch_name ? 'input-error' : ''}
                  />
                  {fieldErrors.batch_name && <span className="field-error">{fieldErrors.batch_name}</span>}
                </div>


                <div className={`form-field ${fieldErrors.start_date ? 'has-error' : ''}`}>
                  <label>📅 Start Date</label>
                  <input
                    name="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    required
                    className={fieldErrors.start_date ? 'input-error' : ''}
                  />
                  {fieldErrors.start_date && <span className="field-error">{fieldErrors.start_date}</span>}
                  {holidayWarning && (
                    <div className="holiday-warning">
                      ⚠️ {holidayWarning}
                    </div>
                  )}
                </div>

                <div className={`form-field ${fieldErrors.programId ? 'has-error' : ''}`}>
                  <label>🎯 Program</label>
                  <select
                    name="programId"
                    value={formData.programId}
                    onChange={handleInputChange}
                    required
                    className={fieldErrors.programId ? 'input-error' : ''}
                  >
                    <option value="">-- Select Program --</option>
                    {programs.map(p => <option key={p.id} value={p.id}>{p.program_name}</option>)}
                  </select>
                  {fieldErrors.programId && <span className="field-error">{fieldErrors.programId}</span>}
                </div>

                <div className={`form-field ${fieldErrors.centerId ? 'has-error' : ''}`}>
                  <label>🏢 Center</label>
                  <select
                    name="centerId"
                    value={formData.centerId}
                    onChange={handleInputChange}
                    required
                    className={fieldErrors.centerId ? 'input-error' : ''}
                  >
                    <option value="">-- Select Center --</option>
                    {centers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  {fieldErrors.centerId && <span className="field-error">{fieldErrors.centerId}</span>}
                </div>

                <div className={`form-field ${fieldErrors.batch_type ? 'has-error' : ''}`}>
                  <label>🕐 Batch Timing</label>
                  <select
                    name="batch_type"
                    value={formData.batch_type}
                    onChange={handleInputChange}
                    className={fieldErrors.batch_type ? 'input-error' : ''}
                  >
                    <option value="morning">🌅 Morning (10:00 AM - 1:00 PM)</option>
                    <option value="afternoon">🌤️ Afternoon (2:00 PM - 5:00 PM)</option>
                    <option value="custom">⏰ Custom Time</option>
                  </select>
                  {fieldErrors.batch_type && <span className="field-error">{fieldErrors.batch_type}</span>}
                </div>

                {/* Custom Time Inputs - shown only when batch_type is 'custom' */}
                {formData.batch_type === 'custom' && (
                  <div className="custom-time-row">
                    <div className={`form-field ${fieldErrors.start_time ? 'has-error' : ''}`}>
                      <label>⏰ Start Time</label>
                      <input
                        type="time"
                        name="start_time_input"
                        value={convertTo24Hour(formData.start_time)}
                        onChange={handleInputChange}
                        className={fieldErrors.start_time ? 'input-error' : ''}
                      />
                      <small className="time-display">{formData.start_time}</small>
                      {fieldErrors.start_time && <span className="field-error">{fieldErrors.start_time}</span>}
                    </div>
                    <div className={`form-field ${fieldErrors.end_time ? 'has-error' : ''}`}>
                      <label>⏰ End Time</label>
                      <input
                        type="time"
                        name="end_time_input"
                        value={convertTo24Hour(formData.end_time)}
                        onChange={handleInputChange}
                        className={fieldErrors.end_time ? 'input-error' : ''}
                      />
                      <small className="time-display">{formData.end_time}</small>
                      {fieldErrors.end_time && <span className="field-error">{fieldErrors.end_time}</span>}
                    </div>
                  </div>
                )}

                <div className={`form-field ${fieldErrors.coordinatorId ? 'has-error' : ''}`}>
                  <label>👨‍💼 Coordinator</label>
                  <select
                    name="coordinatorId"
                    value={formData.coordinatorId}
                    onChange={handleInputChange}
                    className={fieldErrors.coordinatorId ? 'input-error' : ''}
                  >
                    <option value="">-- Select Coordinator (Optional) --</option>
                    {trainers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  {fieldErrors.coordinatorId && <span className="field-error">{fieldErrors.coordinatorId}</span>}
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={handleCloseModal}>
                  ❌ Cancel
                </button>
                <button type="submit" className="submit-btn">
                  {isEditing ? '💾 Save Changes' : '➕ Create Batch'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchManagement;